import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { Camera, VideoCamera, Microphone, Envelope, FilmStrip, Image as ImageIcon, Users, Gear, Plus, Trash, PaperPlaneRight, Check, X, Clock, ArrowLeft, FileText, PaintBrush } from '@phosphor-icons/react';
import { EventDropNav } from '../components/EventDropLogo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvitesPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('elegant-dark');
  const [tab, setTab] = useState('guests'); // guests | templates | send
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState(new Set());
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [autoSend, setAutoSend] = useState(true); // Auto-fire invite on Add when email/phone present
  const [resendingId, setResendingId] = useState(null); // guest_id currently being re-sent
  // Wedding-only evening overrides — when set, evening cohort uses these
  // instead of the main template + customMessage. `null` template = "same
  // as main", non-null = explicit choice. Empty string message = "same as
  // main", non-empty = use this string.
  const [eveningTemplate, setEveningTemplate] = useState(null);
  const [eveningMessage, setEveningMessage] = useState('');
  const [showEveningOverride, setShowEveningOverride] = useState(false);
  // Set true once the page has finished hydrating wedding overrides from the
  // server. Guards the auto-save effect from racing the load and writing
  // empty defaults over persisted values during the initial render burst.
  const [overridesHydrated, setOverridesHydrated] = useState(false);
  // Hydration flag for the main `customMessage` draft. Same race-prevention
  // pattern as `overridesHydrated`.
  const [customMessageHydrated, setCustomMessageHydrated] = useState(false);
  // Auto-save status indicator. Shows a fade-in "Saved" pill for ~1.5s after
  // every successful PUT so the organiser knows their copy is safe without
  // having to reload to check.
  const [savedFlash, setSavedFlash] = useState(false);
  // Separate flash for the main custom-message field — keeps the visual
  // feedback anchored to whichever field actually saved.
  const [mainSavedFlash, setMainSavedFlash] = useState(false);
  // Wedding category filter — drives both the displayed list and the default
  // category for newly-added guests. Values: 'all' | 'wedding_reception' |
  // 'evening_reception'. Inert (always 'all') for non-wedding events.
  const [categoryFilter, setCategoryFilter] = useState('all');
  const csvRef = useRef(null);

  // Wedding detection: explicit `event_type === 'wedding'` OR a wedding
  // keyword in the name (covers events created before the type field was
  // introduced).
  const isWedding = (() => {
    if (!event) return false;
    if (event.event_type === 'wedding') return true;
    const n = (event.event_name || '').toLowerCase();
    return /wedding|engagement|anniversary/.test(n);
  })();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        // Hydrate persisted wedding evening overrides FIRST, then mark
        // overrides as hydrated, then set the event. This ordering keeps
        // the auto-save effect from firing with stale (default null/empty)
        // values during the initial render burst.
        if (r.data?.evening_invite_template) setEveningTemplate(r.data.evening_invite_template);
        if (r.data?.evening_invite_message) setEveningMessage(r.data.evening_invite_message);
        if (r.data?.custom_invite_message) setCustomMessage(r.data.custom_invite_message);
        setOverridesHydrated(true);
        setCustomMessageHydrated(true);
        setEvent(r.data);
        const [g, s, t, sms] = await Promise.all([
          axios.get(`${API}/events/${r.data.event_id}/guests`),
          axios.get(`${API}/events/${r.data.event_id}/invites/stats`),
          axios.get(`${API}/invite-templates`),
          axios.get(`${API}/sms-status`),
        ]);
        setGuests(g.data); setStats(s.data); setTemplates(t.data);
        setSmsEnabled(sms.data?.configured || false);
      } catch { setNotFound(true); }
    })();
  }, [slug]);

  // Debounced auto-save of the wedding evening overrides. Fires 600ms after
  // the last keystroke / dropdown change so we don't hammer the API while
  // someone is typing. Skipped until overrides have been hydrated from the
  // server (prevents a clobbering write during initial mount).
  useEffect(() => {
    if (!event?.event_id || !isWedding || !overridesHydrated) return;
    // Only persist when this differs from what was just hydrated.
    const sameAsLoaded =
      (eveningTemplate || null) === (event.evening_invite_template || null) &&
      (eveningMessage || '') === (event.evening_invite_message || '');
    if (sameAsLoaded) return;
    const t = setTimeout(() => {
      axios.put(`${API}/events/${event.event_id}/wedding-invite-overrides`, {
        evening_template: eveningTemplate,
        evening_message: eveningMessage,
      }).then(() => {
        setSavedFlash(true);
        // Auto-clear after 1.5s so the indicator only celebrates the most
        // recent save, not all of them stacked.
        setTimeout(() => setSavedFlash(false), 1500);
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eveningTemplate, eveningMessage, event?.event_id, isWedding, overridesHydrated]);

  // Debounced auto-save for the main `Custom Message (optional)` draft.
  // Same 600ms debounce + Saved-flash pattern as the evening overrides,
  // but applies to ALL events (not wedding-only).
  useEffect(() => {
    if (!event?.event_id || !customMessageHydrated) return;
    const sameAsLoaded = (customMessage || '') === (event.custom_invite_message || '');
    if (sameAsLoaded) return;
    const t = setTimeout(() => {
      axios.put(`${API}/events/${event.event_id}/custom-invite-message`, {
        custom_message: customMessage,
      }).then(() => {
        setMainSavedFlash(true);
        setTimeout(() => setMainSavedFlash(false), 1500);
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customMessage, event?.event_id, customMessageHydrated]);

  const refresh = async () => {
    if (!event) return;
    const [g, s] = await Promise.all([
      axios.get(`${API}/events/${event.event_id}/guests`),
      axios.get(`${API}/events/${event.event_id}/invites/stats`),
    ]);
    setGuests(g.data); setStats(s.data);
  };

  // Live RSVP toasts — backend emits `rsvp_update` to room `event:{event_id}`
  // whenever a guest accepts/declines from the public RSVP page.
  useEffect(() => {
    if (!event?.event_id) return;
    const socket = io(BACKEND_URL, { path: '/api/socket.io', transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('event_join', { event_id: event.event_id });
    });
    socket.on('rsvp_update', (payload) => {
      const name = payload?.guest_name || 'Guest';
      const plus = payload?.plus_one ? ' (+1)' : '';
      if (payload?.status === 'attending') {
        toast.success(`🎉 ${name} just accepted${plus}`, { duration: 6000 });
      } else if (payload?.status === 'declined') {
        toast(`${name} declined`, { icon: '😔', duration: 5000 });
      } else if (payload?.status === 'maybe') {
        toast(`${name} replied 'maybe'`, { icon: '🤔', duration: 5000 });
      }
      // Refresh stats + guest list so counters and "Sent" badges stay in sync.
      (async () => {
        try {
          const [g, s] = await Promise.all([
            axios.get(`${API}/events/${event.event_id}/guests`),
            axios.get(`${API}/events/${event.event_id}/invites/stats`),
          ]);
          setGuests(g.data); setStats(s.data);
        } catch { /* noop */ }
      })();
    });
    return () => {
      try { socket.emit('event_leave', { event_id: event.event_id }); } catch { /* noop */ }
      socket.disconnect();
    };
  }, [event?.event_id]);
  const addGuest = async () => {
    if (!newName.trim() || !event) return;
    try {
      // For wedding events: if the organiser is filtering on a specific
      // category, new guests inherit it. On "all", new guests default to
      // wedding_reception (set server-side).
      const categoryToSet = isWedding && categoryFilter !== 'all' ? categoryFilter : undefined;
      const res = await axios.post(`${API}/events/${event.event_id}/guests`, {
        name: newName,
        email: newEmail || null,
        phone: newPhone || null,
        invite_category: categoryToSet,
      });
      const created = res.data;
      const trimmedEmail = (newEmail || '').trim();
      const trimmedPhone = (newPhone || '').trim();
      // Auto-send invite immediately when toggle is ON and we have a contact method.
      // Email + SMS fire in parallel; either failing doesn't roll back the guest add.
      let sentEmail = false;
      let sentSms = false;
      if (autoSend && created?.guest_id) {
        const sends = [];
        if (trimmedEmail) {
          sends.push(
            axios.post(`${API}/events/${event.event_id}/invites/send`, {
              guest_ids: [created.guest_id],
              template_id: selectedTemplate,
              message: customMessage,
            }).then(r => { sentEmail = (r.data?.sent || 0) > 0; }).catch(() => {})
          );
        }
        if (trimmedPhone && smsEnabled) {
          sends.push(
            axios.post(`${API}/events/${event.event_id}/invites/send-sms`, {
              guest_ids: [created.guest_id],
            }).then(r => { sentSms = (r.data?.sent || 0) > 0; }).catch(() => {})
          );
        }
        if (sends.length) await Promise.all(sends);
      }
      setNewName(''); setNewEmail(''); setNewPhone('');
      if (sentEmail && sentSms) toast.success('Guest added — email + SMS invite sent');
      else if (sentEmail) toast.success('Guest added — email invite sent');
      else if (sentSms) toast.success('Guest added — SMS invite sent');
      else if (autoSend && (trimmedEmail || trimmedPhone)) toast.success('Guest added (invite could not be sent — try Send tab)');
      else toast.success('Guest added');
      refresh();
    } catch { toast.error('Failed to add guest'); }
  };

  const removeGuest = async (gid) => {
    if (!event) return;
    await axios.delete(`${API}/events/${event.event_id}/guests/${gid}`);
    setSelectedGuests(prev => { const n = new Set(prev); n.delete(gid); return n; });
    refresh();
  };

  // One-click re-fire for a single guest. Uses currently-selected template +
  // custom message. Sends email + SMS (if enabled & phone present) in parallel.
  const resendGuest = async (g) => {
    if (!event || !g) return;
    if (!g.email && !g.phone) {
      toast.error('Add an email or phone first');
      return;
    }
    setResendingId(g.guest_id);
    let emailOk = false, smsOk = false;
    const calls = [];
    if (g.email) {
      calls.push(
        axios.post(`${API}/events/${event.event_id}/invites/send`, {
          guest_ids: [g.guest_id], template_id: selectedTemplate, message: customMessage,
        }).then(r => { emailOk = (r.data?.sent || 0) > 0; }).catch(() => {})
      );
    }
    if (g.phone && smsEnabled) {
      calls.push(
        axios.post(`${API}/events/${event.event_id}/invites/send-sms`, {
          guest_ids: [g.guest_id],
        }).then(r => { smsOk = (r.data?.sent || 0) > 0; }).catch(() => {})
      );
    }
    try {
      await Promise.all(calls);
      if (emailOk && smsOk) toast.success(`Re-sent email + SMS to ${g.name}`);
      else if (emailOk) toast.success(`Re-sent email to ${g.name}`);
      else if (smsOk) toast.success(`Re-sent SMS to ${g.name}`);
      else toast.error(`Could not resend to ${g.name}`);
      refresh();
    } finally {
      setResendingId(null);
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !event) return;
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    const createdIdsWithEmail = [];
    const createdIdsWithPhone = [];
    let added = 0;
    for (const line of lines.slice(lines[0].toLowerCase().includes('name') ? 1 : 0)) {
      const parts = line.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
      const name = parts[0];
      const email = parts[1] || null;
      const phone = parts[2] || null;
      if (!name) continue;
      try {
        const res = await axios.post(`${API}/events/${event.event_id}/guests`, { name, email, phone });
        const gid = res.data?.guest_id;
        if (gid) {
          if (email) createdIdsWithEmail.push(gid);
          if (phone) createdIdsWithPhone.push(gid);
        }
        added++;
      } catch {}
    }
    // Bulk-fire invites for all freshly-added guests when toggle is on.
    let emailSent = 0, smsSent = 0;
    if (autoSend) {
      try {
        if (createdIdsWithEmail.length) {
          const r = await axios.post(`${API}/events/${event.event_id}/invites/send`, {
            guest_ids: createdIdsWithEmail, template_id: selectedTemplate, message: customMessage,
          });
          emailSent = r.data?.sent || 0;
        }
      } catch {}
      try {
        if (createdIdsWithPhone.length && smsEnabled) {
          const r = await axios.post(`${API}/events/${event.event_id}/invites/send-sms`, {
            guest_ids: createdIdsWithPhone,
          });
          smsSent = r.data?.sent || 0;
        }
      } catch {}
    }
    const parts = [`${added} guest${added === 1 ? '' : 's'} imported`];
    if (emailSent) parts.push(`${emailSent} email${emailSent === 1 ? '' : 's'} sent`);
    if (smsSent) parts.push(`${smsSent} SMS sent`);
    toast.success(parts.join(' · '));
    refresh();
    if (csvRef.current) csvRef.current.value = '';
  };

  const toggleSelect = (gid) => {
    setSelectedGuests(prev => { const n = new Set(prev); if (n.has(gid)) n.delete(gid); else n.add(gid); return n; });
  };

  const selectAll = () => {
    // Bulk-select honours the active category filter — selecting "all" on
    // the Evening tab only picks evening guests, not the entire list.
    const cohort = isWedding && categoryFilter !== 'all'
      ? guests.filter(g => g.invite_category === categoryFilter)
      : guests;
    const withEmail = cohort.filter(g => g.email);
    if (selectedGuests.size === withEmail.length && withEmail.every(g => selectedGuests.has(g.guest_id))) {
      setSelectedGuests(new Set());
    } else {
      setSelectedGuests(new Set(withEmail.map(g => g.guest_id)));
    }
  };

  const sendInvites = async () => {
    if (!event || selectedGuests.size === 0) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/invites/send`, {
        guest_ids: [...selectedGuests], template_id: selectedTemplate, message: customMessage
      });
      toast.success(`${res.data.sent} email invites sent!`);
      setSelectedGuests(new Set());
      refresh();
    } catch { toast.error('Failed to send invites'); }
    finally { setSending(false); }
  };

  // Wedding cohort quick-send. Bypasses the multi-select: collects every
  // guest in the given category with an email, optionally skipping anyone
  // already invited, and fires the same /invites/send endpoint with the
  // current template + custom message. For the evening cohort, evening
  // overrides apply when set — falling back to the main pickers when not.
  const sendCohort = async (category, { skipAlreadyInvited = true } = {}) => {
    if (!event || sending) return;
    const cohort = guests.filter(
      g => g.invite_category === category && g.email && (!skipAlreadyInvited || !g.invited_at)
    );
    if (cohort.length === 0) {
      toast.error(skipAlreadyInvited ? 'No new guests in this cohort to invite' : 'No guests with email in this cohort');
      return;
    }
    // Evening cohort honours the override fields when filled in. Empty
    // overrides fall back to the main template/message so the button still
    // works for organisers who don't need different copy.
    const isEvening = category === 'evening_reception';
    const tplId = isEvening && eveningTemplate ? eveningTemplate : selectedTemplate;
    const msg = isEvening && eveningMessage.trim() ? eveningMessage : customMessage;
    setSending(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/invites/send`, {
        guest_ids: cohort.map(g => g.guest_id),
        template_id: tplId,
        message: msg,
      });
      const label = category === 'wedding_reception' ? 'Wedding & Evening' : 'Evening Only';
      toast.success(`${res.data.sent} ${label} invite${res.data.sent === 1 ? '' : 's'} sent`);
      refresh();
    } catch { toast.error('Failed to send cohort invites'); }
    finally { setSending(false); }
  };

  const sendSmsInvites = async () => {
    if (!event || selectedGuests.size === 0) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/invites/send-sms`, {
        guest_ids: [...selectedGuests]
      });
      toast.success(`${res.data.sent} SMS invites sent!`);
      setSelectedGuests(new Set());
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SMS send failed');
    }
    finally { setSending(false); }
  };

  const statusIcon = (s) => {
    if (s === 'attending') return <div className="flex items-center gap-1.5 text-[#B89762] text-sm font-semibold"><Check size={14} weight="bold" /> Attending</div>;
    if (s === 'declined') return <div className="flex items-center gap-1.5 text-red-400 text-sm font-semibold"><X size={14} weight="bold" /> Declined</div>;
    return <div className="flex items-center gap-1.5 text-amber-400 text-sm font-semibold"><Clock size={14} weight="bold" /> Pending</div>;
  };

  const tpl = templates.find(t => t.id === selectedTemplate) || templates[0];

  // Apply wedding category filter to the rendered list. Stats counters at
  // the top continue to reflect totals — the filter only affects which rows
  // appear and which guests get bulk-selected.
  const filteredGuests = isWedding && categoryFilter !== 'all'
    ? guests.filter(g => g.invite_category === categoryFilter)
    : guests;

  if (notFound) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center px-6" data-testid="event-not-found-invites"><div className="rounded-2xl border border-white/10 bg-[#15161B] p-8 text-center max-w-sm"><h2 className="text-xl font-bold text-white mb-2">Can't reach this event</h2><p className="text-white/60 text-sm mb-4">The connection dropped or the event may have ended. Try again, or head back to your dashboard.</p><div className="flex gap-2 justify-center"><button onClick={() => window.location.reload()} className="px-5 py-2.5 rounded-xl bg-[#B89762] text-white text-sm font-semibold">Try again</button><button onClick={() => navigate('/dashboard')} className="px-5 py-2.5 rounded-xl bg-white/10 text-white text-sm font-semibold">Dashboard</button></div></div></div>;
  if (!event) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center" data-testid="invites-loading"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" /></div>;

  return (
    <div className="min-h-screen" style={{ background: '#15161B', fontFamily: 'Inter, sans-serif' }} data-testid="invites-page">
      {/* Header */}
      <div className="border-b border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(`/event/${slug}`)} className="text-white/40 hover:text-white transition-colors flex-shrink-0"><ArrowLeft size={20} weight="bold" /></button>
          <div className="min-w-0">
            <h1 className="text-white font-bold text-lg">Invites</h1>
            <span className="text-white/30 text-xs truncate block">{event.event_name}</span>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="px-4 sm:px-6 py-3 border-b border-white/10 grid grid-cols-5 gap-2" data-testid="invite-stats">
          <div className="text-center"><p className="text-lg sm:text-2xl font-bold text-white">{stats.total}</p><p className="text-[10px] sm:text-xs text-white/40">Total</p></div>
          <div className="text-center"><p className="text-lg sm:text-2xl font-bold text-[#B89762]">{stats.attending}</p><p className="text-[10px] sm:text-xs text-white/40">Attending</p></div>
          <div className="text-center"><p className="text-lg sm:text-2xl font-bold text-red-400">{stats.declined}</p><p className="text-[10px] sm:text-xs text-white/40">Declined</p></div>
          <div className="text-center"><p className="text-lg sm:text-2xl font-bold text-amber-400">{stats.pending}</p><p className="text-[10px] sm:text-xs text-white/40">Pending</p></div>
          <div className="text-center"><p className="text-lg sm:text-2xl font-bold text-blue-400">{stats.invited}</p><p className="text-[10px] sm:text-xs text-white/40">Invited</p></div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 sm:px-6 pt-4 space-y-3" data-testid="invite-tabs">
        <div className="flex gap-2">
          {[{ id: 'guests', label: 'Guest List' }, { id: 'templates', label: 'Template' }, { id: 'send', label: 'Send' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${tab === t.id ? 'text-white' : 'bg-[#15161B] text-white/50 hover:text-white'}`}
              style={tab === t.id ? { background: 'linear-gradient(135deg, #B89762, #C29D52)' } : {}}
              data-testid={`tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
        <button onClick={() => navigate(`/event/${slug}/invites/editor`)}
          className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
          style={{ background: 'linear-gradient(135deg, #B89762, #A855F7)', color: 'white' }} data-testid="design-invite-btn">
          <PaintBrush size={16} weight="bold" /> Design Invite
        </button>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* GUEST LIST TAB */}
        {tab === 'guests' && (
          <div data-testid="guest-list-tab">
            {/* WEDDING CATEGORY FILTER — only renders for wedding events */}
            {isWedding && (
              <div className="mb-3 p-1 rounded-xl bg-[#15161B] border border-white/5 flex gap-1" data-testid="wedding-category-filter">
                {[
                  { id: 'all', label: 'All', count: stats?.total ?? guests.length },
                  { id: 'wedding_reception', label: 'Wedding & Evening', count: stats?.wedding_full ?? 0 },
                  { id: 'evening_reception', label: 'Evening Only', count: stats?.evening_only ?? 0 },
                ].map(opt => {
                  const active = categoryFilter === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setCategoryFilter(opt.id)}
                      className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                        active
                          ? 'text-white'
                          : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                      }`}
                      style={active ? { background: 'linear-gradient(135deg, #B89762, #C29D52)' } : {}}
                      data-testid={`category-tab-${opt.id}`}
                    >
                      <span>{opt.label}</span>
                      <span className={`ml-2 text-xs ${active ? 'text-white/80' : 'text-white/30'}`}>{opt.count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Auto-send toggle — when ON, invite fires the moment Add is clicked */}
            <div className="flex items-center justify-between mb-3 px-4 py-3 rounded-xl bg-[#15161B] border border-white/5">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold flex items-center gap-2">
                  <PaperPlaneRight size={14} weight="bold" className="text-[#B89762] flex-shrink-0" />
                  Send invite immediately
                </p>
                <p className="text-white/40 text-xs mt-0.5">
                  When on, an email{smsEnabled ? ' or SMS' : ''} fires the moment you click Add.
                  {!smsEnabled && ' SMS requires Twilio setup.'}
                  {isWedding && categoryFilter !== 'all' && (
                    <> · New guests will be added to <strong className="text-white/70">{categoryFilter === 'wedding_reception' ? 'Wedding & Evening' : 'Evening Only'}</strong>.</>
                  )}
                </p>
              </div>
              <button
                onClick={() => setAutoSend(v => !v)}
                role="switch"
                aria-checked={autoSend}
                data-testid="auto-send-toggle"
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-3 ${autoSend ? 'bg-gradient-to-r from-[#B89762] to-[#B89762]' : 'bg-white/15'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${autoSend ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            {/* Add guest form */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Guest name"
                className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/50" data-testid="guest-name-input" />
              <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email (optional)"
                className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/50" data-testid="guest-email-input" />
              <input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="Phone (optional)"
                className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/50" data-testid="guest-phone-input" />
              <div className="flex gap-2">
                <button onClick={addGuest} className="flex-1 py-3 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 hover:scale-105 transition-all" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="add-guest-btn">
                  <Plus size={16} weight="bold" /> Add
                </button>
                <button onClick={() => csvRef.current?.click()} className="px-4 py-3 rounded-xl font-semibold text-white/60 text-sm flex items-center gap-2 bg-[#15161B] border border-white/10 hover:bg-white/10 transition-colors" data-testid="import-csv-btn">
                  <FileText size={16} weight="bold" /> CSV
                </button>
              </div>
              <input ref={csvRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleCsvImport} />
            </div>

            {/* Guest list */}
            {filteredGuests.length === 0 ? (
              <div className="text-center py-16 text-white/30"><Users size={48} weight="bold" className="mx-auto mb-3 opacity-30" />
                <p>{isWedding && categoryFilter !== 'all'
                  ? `No guests in ${categoryFilter === 'wedding_reception' ? 'Wedding & Evening' : 'Evening Only'} yet`
                  : 'No guests yet — add your first guest above'}
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="guest-list">
                {filteredGuests.map(g => {
                  const canSend = !!(g.email || (g.phone && smsEnabled));
                  const busy = resendingId === g.guest_id;
                  return (
                  <div key={g.guest_id} className="flex items-center gap-4 px-5 py-4 rounded-xl bg-[#15161B] border border-white/5 hover:border-white/10 transition-colors" data-testid={`guest-${g.guest_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{g.name}</p>
                      <p className="text-white/30 text-xs truncate">{g.email || g.phone || 'No email or phone'}</p>
                    </div>
                    {/* Wedding category pill — click to flip between full-day and evening */}
                    {isWedding && (
                      <button
                        onClick={async () => {
                          const next = g.invite_category === 'evening_reception' ? 'wedding_reception' : 'evening_reception';
                          try {
                            await axios.put(`${API}/events/${event.event_id}/guests/${g.guest_id}`, { invite_category: next });
                            toast.success(`Moved to ${next === 'wedding_reception' ? 'Wedding & Evening' : 'Evening Only'}`);
                            refresh();
                          } catch { toast.error('Could not update'); }
                        }}
                        title="Click to change category"
                        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors flex-shrink-0 ${
                          g.invite_category === 'evening_reception'
                            ? 'text-amber-300 bg-amber-500/10 hover:bg-amber-500/20'
                            : 'text-[#C29D52] bg-[#B89762]/10 hover:bg-[#B89762]/85/20'
                        }`}
                        data-testid={`category-pill-${g.guest_id}`}
                      >
                        {g.invite_category === 'evening_reception' ? 'Evening' : 'Full day'}
                      </button>
                    )}
                    {statusIcon(g.rsvp_status)}
                    {g.invited_at && <span className="text-white/20 text-xs">Sent</span>}
                    <button
                      onClick={() => resendGuest(g)}
                      disabled={!canSend || busy}
                      title={canSend ? (g.invited_at ? 'Resend invite' : 'Send invite') : 'Add an email or phone first'}
                      className="p-2 rounded-lg text-white/40 hover:text-[#C29D52] hover:bg-[#B89762]/85/10 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white/40"
                      data-testid={`resend-${g.guest_id}`}
                    >
                      <PaperPlaneRight size={16} weight="bold" className={busy ? 'animate-pulse' : ''} />
                    </button>
                    <button onClick={() => removeGuest(g.guest_id)} className="p-2 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`remove-${g.guest_id}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                );})}
              </div>
            )}
          </div>
        )}

        {/* TEMPLATES TAB */}
        {tab === 'templates' && (
          <div data-testid="templates-tab">
            <p className="text-white/40 text-sm mb-4">Choose a style for your invites</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {templates.map(t => (
                <button key={t.id} onClick={() => setSelectedTemplate(t.id)}
                  className={`rounded-2xl overflow-hidden border-2 transition-all hover:scale-105 ${selectedTemplate === t.id ? 'border-[#B89762]/50 ring-2 ring-[#B89762]/30' : 'border-white/10'}`}
                  data-testid={`template-${t.id}`}>
                  <div className="aspect-[3/4] flex flex-col items-center justify-center p-6 text-center"
                    style={{ background: t.bg.startsWith('linear') ? t.bg : t.bg, color: t.text_color }}>
                    <p className="text-xs opacity-50 mb-2">You're Invited</p>
                    <p className="font-bold text-lg mb-1" style={{ fontFamily: t.font }}>Event Name</p>
                    <p className="text-xs opacity-60 mb-4">Date</p>
                    <div className="px-4 py-1.5 rounded-full text-xs font-bold" style={{ background: t.accent, color: 'white' }}>RSVP</div>
                  </div>
                  <div className="px-3 py-2 bg-black/50 text-center">
                    <p className="text-white text-xs font-semibold">{t.name}</p>
                    <p className="text-white/30 text-[10px]">{t.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SEND TAB */}
        {tab === 'send' && (
          <div data-testid="send-tab">
            {/* Template preview */}
            {tpl && (
              <div className="mb-6 rounded-2xl overflow-hidden max-w-sm mx-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div className="aspect-[3/4] flex flex-col items-center justify-center p-8 text-center" style={{ background: tpl.bg, color: tpl.text_color }}>
                  <p className="text-sm opacity-50 mb-3">You're Invited</p>
                  <p className="font-bold text-2xl mb-2" style={{ fontFamily: tpl.font }}>{event.event_name}</p>
                  {event.event_date && <p className="text-sm opacity-60 mb-2">{event.event_date}</p>}
                  {customMessage && <p className="text-sm opacity-70 mt-2 italic">{customMessage}</p>}
                  <div className="px-6 py-2.5 rounded-full text-sm font-bold mt-6" style={{ background: tpl.accent, color: 'white' }}>RSVP Now</div>
                </div>
              </div>
            )}

            {/* Custom message */}
            <div className="max-w-sm mx-auto mb-6">
              <label className="flex items-center gap-2 text-xs text-white/40 font-semibold mb-2">
                <span>Custom Message (optional)</span>
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.06em] bg-emerald-500/15 text-emerald-300 transition-opacity duration-300 ${mainSavedFlash ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                  data-testid="custom-message-saved-flash"
                  aria-live="polite"
                >
                  <Check size={9} weight="bold" />
                  Saved
                </span>
              </label>
              <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)} placeholder="Add a personal note..."
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/50 resize-none" rows={3} data-testid="custom-message" />
            </div>

            {/* WEDDING QUICK-SEND — one-click bulk to a whole cohort. Counts
                show "N awaiting" so the organiser knows what they're about
                to fire without scrolling through the manual selector. */}
            {isWedding && (() => {
              const fullDayPending = guests.filter(g => g.invite_category === 'wedding_reception' && g.email && !g.invited_at).length;
              const fullDayTotal = guests.filter(g => g.invite_category === 'wedding_reception' && g.email).length;
              const eveningPending = guests.filter(g => g.invite_category === 'evening_reception' && g.email && !g.invited_at).length;
              const eveningTotal = guests.filter(g => g.invite_category === 'evening_reception' && g.email).length;
              return (
                <div className="max-w-lg mx-auto mb-6 p-4 rounded-2xl bg-[#15161B] border border-white/5" data-testid="wedding-cohort-send">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/40 font-bold mb-3">Wedding quick-send</p>
                  <div className="space-y-2">
                    <button
                      onClick={() => sendCohort('wedding_reception')}
                      disabled={sending || fullDayPending === 0}
                      className="w-full px-4 py-3 rounded-xl flex items-center justify-between gap-3 border border-[#B89762]/50/30 bg-[#B89762]/10 hover:bg-[#B89762]/85/15 transition-colors disabled:opacity-40 disabled:hover:bg-[#B89762]/85/10"
                      data-testid="send-cohort-wedding"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <PaperPlaneRight size={14} weight="bold" className="text-[#C29D52] flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-white text-sm font-semibold truncate">Send to Wedding & Evening</p>
                          <p className="text-white/40 text-xs truncate">
                            {fullDayPending > 0
                              ? `${fullDayPending} awaiting${fullDayTotal > fullDayPending ? ` · ${fullDayTotal - fullDayPending} already sent` : ''}`
                              : fullDayTotal > 0 ? `All ${fullDayTotal} already invited` : 'No guests with email yet'}
                          </p>
                        </div>
                      </div>
                      <span className="text-[#C29D52] text-xs font-bold flex-shrink-0">{fullDayPending || ''}</span>
                    </button>
                    <button
                      onClick={() => sendCohort('evening_reception')}
                      disabled={sending || eveningPending === 0}
                      className="w-full px-4 py-3 rounded-xl flex items-center justify-between gap-3 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15 transition-colors disabled:opacity-40 disabled:hover:bg-amber-500/10"
                      data-testid="send-cohort-evening"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <PaperPlaneRight size={14} weight="bold" className="text-amber-300 flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-white text-sm font-semibold truncate">Send to Evening Only</p>
                          <p className="text-white/40 text-xs truncate">
                            {eveningPending > 0
                              ? `${eveningPending} awaiting${eveningTotal > eveningPending ? ` · ${eveningTotal - eveningPending} already sent` : ''}`
                              : eveningTotal > 0 ? `All ${eveningTotal} already invited` : 'No guests with email yet'}
                          </p>
                        </div>
                      </div>
                      <span className="text-amber-300 text-xs font-bold flex-shrink-0">{eveningPending || ''}</span>
                    </button>

                    {/* Evening copy overrides — collapsed by default. Lets
                        the organiser tailor the evening invite (different
                        timing, no ceremony details) without affecting the
                        full-day blast. Empty overrides fall back to the
                        main template + message above. */}
                    <div className="rounded-xl bg-[#15161B] border border-white/5 overflow-hidden">
                      <button
                        onClick={() => setShowEveningOverride(v => !v)}
                        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-white/[0.03] transition-colors"
                        data-testid="evening-override-toggle"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-white/70 text-xs font-semibold truncate flex items-center gap-2">
                            <span className="truncate">
                              {eveningTemplate || eveningMessage.trim()
                                ? '✓ Custom evening copy active'
                                : 'Customise evening invite separately'}
                            </span>
                            {/* Auto-save flash — fades in/out for 1.5s after
                                every successful PUT, like Google Docs. */}
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.06em] bg-emerald-500/15 text-emerald-300 transition-opacity duration-300 flex-shrink-0 ${savedFlash ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                              data-testid="override-saved-flash"
                              aria-live="polite"
                            >
                              <Check size={9} weight="bold" />
                              Saved
                            </span>
                          </p>
                          <p className="text-white/30 text-[10px] truncate">
                            {showEveningOverride ? 'Tap to collapse' : 'Different timing, no ceremony — optional'}
                          </p>
                        </div>
                        <span className="text-white/40 text-xs ml-2 flex-shrink-0">{showEveningOverride ? '−' : '+'}</span>
                      </button>
                      {showEveningOverride && (
                        <div className="p-3 pt-1 space-y-3 border-t border-white/5">
                          <div>
                            <label className="block text-[10px] text-white/40 font-bold uppercase tracking-[0.12em] mb-1.5">Evening template</label>
                            <select
                              value={eveningTemplate || ''}
                              onChange={(e) => setEveningTemplate(e.target.value || null)}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-500"
                              data-testid="evening-template-select"
                            >
                              <option value="">Same as main template</option>
                              {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] text-white/40 font-bold uppercase tracking-[0.12em] mb-1.5">Evening message</label>
                            <textarea
                              value={eveningMessage}
                              onChange={(e) => setEveningMessage(e.target.value)}
                              placeholder="e.g. Join us from 7pm for drinks, dancing and dessert..."
                              rows={3}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-white/25 focus:outline-none focus:border-amber-500 resize-none"
                              data-testid="evening-message-input"
                            />
                            {eveningMessage && (
                              <button
                                onClick={() => { setEveningMessage(''); setEveningTemplate(null); }}
                                className="text-[10px] text-white/40 hover:text-white/70 mt-1.5"
                                data-testid="evening-override-clear"
                              >
                                Clear evening overrides
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-white/30 text-[10px] mt-3 text-center">Uses the template + custom message above. Skips anyone already invited.</p>
                </div>
              );
            })()}

            {/* Select guests */}
            <div className="max-w-lg mx-auto">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/60 text-sm font-semibold">Select guests to invite</p>
                <button onClick={selectAll} className="text-xs text-[#B89762] font-semibold hover:text-[#C29D52]" data-testid="select-all">{selectedGuests.size === guests.filter(g => g.email).length ? 'Deselect All' : 'Select All'}</button>
              </div>
              <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                {filteredGuests.filter(g => g.email).map(g => (
                  <div key={g.guest_id} onClick={() => toggleSelect(g.guest_id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all ${selectedGuests.has(g.guest_id) ? 'bg-[#B89762]/10 border border-[#B89762]/50/30' : 'bg-[#15161B] border border-white/5 hover:border-white/10'}`}>
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selectedGuests.has(g.guest_id) ? 'bg-[#B89762] border-[#B89762]/50' : 'border-white/20'}`}>
                      {selectedGuests.has(g.guest_id) && <Check size={12} weight="bold" className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{g.name}</p>
                      <p className="text-white/30 text-xs truncate">{g.email}</p>
                    </div>
                    {isWedding && g.invite_category && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
                        g.invite_category === 'evening_reception' ? 'text-amber-300 bg-amber-500/10' : 'text-[#C29D52] bg-[#B89762]/10'
                      }`}>
                        {g.invite_category === 'evening_reception' ? 'Evening' : 'Full day'}
                      </span>
                    )}
                    {g.invited_at && <span className="text-white/20 text-xs">Already sent</span>}
                  </div>
                ))}
                {filteredGuests.filter(g => g.email).length === 0 && (
                  <p className="text-center text-white/30 text-sm py-8">
                    {isWedding && categoryFilter !== 'all'
                      ? `No guests in ${categoryFilter === 'wedding_reception' ? 'Wedding & Evening' : 'Evening Only'} have an email yet`
                      : 'Add guests with email addresses first'}
                  </p>
                )}
              </div>

              {/* Send buttons */}
              <div className="space-y-3">
                <button onClick={sendInvites} disabled={sending || selectedGuests.size === 0}
                  className="w-full py-4 rounded-2xl font-bold text-white text-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="send-invites-btn">
                  <PaperPlaneRight size={20} weight="bold" />
                  {sending ? 'Sending...' : `Send ${selectedGuests.size} Email Invite${selectedGuests.size !== 1 ? 's' : ''}`}
                </button>
                {smsEnabled && (
                  <button onClick={sendSmsInvites} disabled={sending || selectedGuests.size === 0}
                    className="w-full py-4 rounded-2xl font-bold text-white text-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-40 border border-white/10"
                    style={{ background: 'rgba(255,255,255,0.05)' }} data-testid="send-sms-btn">
                    <PaperPlaneRight size={20} weight="bold" />
                    {sending ? 'Sending...' : `Send ${selectedGuests.size} SMS Invite${selectedGuests.size !== 1 ? 's' : ''}`}
                  </button>
                )}
                {!smsEnabled && (
                  <p className="text-center text-white/20 text-xs">SMS sending requires Twilio setup. Add credentials to enable.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitesPage;
